import LoadingButton from "@mui/lab/LoadingButton";
import { Alert, Box, Collapse, Radio, Typography, useTheme } from "@mui/material";
import Button from "@mui/material/Button";
import { useLiveQuery } from "@tanstack/react-db";
import { Base64 } from "js-base64";
import { DateTime } from "luxon";
import React, { type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import ButtonTabs from "@/components/ButtonTabs.tsx";
import ConfirmDiscardChanges from "@/components/ModalForms/ConfirmDiscardChanges.tsx";
import SelectRecipients from "@/components/SelectRecipients.tsx";
import TextMessageInput from "@/components/TextMessageInput.tsx";
import HandleFileUpload from "@/components/UploadFiles/HandleFileUpload.tsx";
import { FullInput } from "@/components/Utils.tsx";
import {
  calculateCron,
  calculateScheduledDateOrCron,
  modalTabs,
  roundTimeToNearest15Minutes,
  ScheduledWorkflowItem,
} from "@/components/workflowUtils";
import { createLogger } from "@/logger";
import {
  createScheduledWorkflowItem,
  createWorkflowItem,
  updateScheduledWorkflowItem,
} from "@/models/actions/clientActions.ts";
import { broadcastRecipientsListQuery } from "@/models/repositories";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider.tsx";
import { UppyContext } from "@/models/UppyContextProvider.tsx";
import { BroadcastRecipientList } from "@/store/types";
import { sundayFirstDayNumber, withDayOfWeek } from "@/utils/date";
import DaysOfWeekPicker from "./DaysOfWeekPicker";
import ScheduleDateTimePicker from "./Scheduling/ScheduleDateTimePicker";

const log = createLogger("feed");

export default function WorkflowModalData({
  askToConfirmClose,
  activeTabState,
  workflowItem,
  closeModal,
  setHasChanges,
  hiddenTabs,
}: {
  askToConfirmClose: boolean;
  activeTabState: string | null;
  closeModal: (value: boolean) => void;
  setHasChanges: (value: boolean) => void;
  workflowItem?: any;
  hiddenTabs?: string[];
}) {
  const theme = useTheme();
  const { currentWorkspaceId: workspaceId } = useContext(WorkspaceContext);
  const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const { addedFile, removeMetaData, removeFiles, addMetaData, uppyClient } = useContext(UppyContext);

  // Modal-local form state. These were previously sourced from the app-wide SchedulingContext, which
  // caused every keystroke to re-render every consumer of that context across the workspace shell.
  const [displayName, setDisplayName] = useState<string>(workflowItem?.displayName ?? "");
  const [scheduledText, setScheduledText] = useState<string>("");

  // Only fetch existing broadcast recipients when editing an existing workflow item. For new
  // workflows there is no broadcast yet, and the empty-broadcastId variant of the query would
  // scan the entire workspace's broadcast_recipient table for no benefit.
  const editingBroadcastId = workflowItem?.broadcast?.broadcastId;
  const { data: availableRecipientRows } = useLiveQuery((q) =>
    workspaceId ? broadcastRecipientsListQuery(workspaceId, editingBroadcastId)(q) : null,
  );
  const availableRecipients = availableRecipientRows ?? [];

  const doesRepeatBool = !!workflowItem?.scheduledTrigger?.cronSchedule;
  const dayOfWeekValue = doesRepeatBool ? sundayFirstDayNumber(workflowItem?.scheduledDate) : null;
  const [activeTab, setActiveTab] = useState<string | null>(activeTabState);
  const [textValid, setTextValid] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [confirmDiscard, setConfirmDiscard] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [recipients, setRecipients] = useState<BroadcastRecipientList[]>(
    workflowItem?.recipients || availableRecipients,
  );
  const [date, setDate] = useState<Date>(workflowItem?.scheduledDate ?? null);
  const [doesRepeat, setDoesRepeat] = useState<boolean>(doesRepeatBool);
  const [dayOfWeek, setDayOfWeek] = useState<number | null>(dayOfWeekValue);

  const hasValidSchedule =
    date != null &&
    DateTime.fromJSDate(date).isValid &&
    (doesRepeat ? typeof dayOfWeek === "number" && !Number.isNaN(dayOfWeek) : true);

  const cronTime = doesRepeat
    ? calculateCron(date, dayOfWeek, timezone)
    : {
        scheduledDate: date != null && DateTime.fromJSDate(date).isValid ? date.toISOString() : undefined,
        scheduledCron: "",
      };
  const hasRecipients = recipients?.some((r: { id?: string }) => Boolean(r?.id)) ?? false;
  const hasMediaOrGeneratedAudio = !!(
    ((addedFile && addedFile.id) || (scheduledText && scheduledText.length > 0)) &&
    displayName &&
    displayName.length > 0
  );
  const canPublish = hasValidSchedule && hasRecipients && hasMediaOrGeneratedAudio && !error;
  const updateSchedule = hasRecipients && hasValidSchedule;
  const feedIds = (recipients?.map((i: { id?: string }) => i.id).filter(Boolean) ?? []) as string[];
  const originalScheduledDate = workflowItem?.scheduledDate != null ? new Date(workflowItem.scheduledDate) : null;
  const original =
    originalScheduledDate != null && DateTime.fromJSDate(originalScheduledDate).isValid
      ? originalScheduledDate.toISOString()
      : undefined;
  const updated = cronTime?.scheduledDate?.toString();
  const internalNameCharLimit = 40;
  const boxHeight = "370px";

  useEffect(() => {
    if (askToConfirmClose === true) {
      setConfirmDiscard(() => askToConfirmClose);
    }
  }, [askToConfirmClose]);

  const changedDateTimeCron = !doesRepeat
    ? original !== updated
    : workflowItem?.scheduledTrigger?.cronSchedule !== cronTime?.scheduledCron;

  const hasDateChange = workflowItem?.id ? changedDateTimeCron : date !== null;

  const availableRecipientMap = availableRecipients
    ?.map((i) => i.feedId)
    ?.sort()
    ?.join("-");
  const recipientMap = recipients
    ?.map((i) => i.id)
    ?.sort()
    ?.join("-");
  const hasRecipientChanges = workflowItem?.id ? availableRecipientMap !== recipientMap : hasRecipients;

  const editingWorkflowId = workflowItem?.id;
  const originalDisplayName = workflowItem?.displayName ?? "";

  const hasChangedValues = useMemo(() => {
    const hasDisplayNameChange = editingWorkflowId
      ? displayName !== originalDisplayName
      : (displayName?.length ?? 0) > 0;

    return [
      { key: "hasScheduledText", value: scheduledText?.length > 0 },
      { key: "hasDisplayName", value: hasDisplayNameChange },
      { key: "hasAddedFile", value: !!addedFile?.id },
      { key: "hasDateChange", value: hasDateChange },
      { key: "hasDoesRepeatChange", value: doesRepeat !== doesRepeatBool },
      { key: "hasRecipientChanges", value: hasRecipientChanges },
    ].filter((i) => i.value);
  }, [
    scheduledText,
    displayName,
    editingWorkflowId,
    originalDisplayName,
    addedFile,
    hasDateChange,
    doesRepeat,
    doesRepeatBool,
    hasRecipientChanges,
  ]);

  const hasChanges = hasChangedValues?.length > 0;

  useEffect(() => {
    if (addedFile?.meta?.feedId) {
      removeMetaData("feedId");
    }
    if (addedFile?.id && displayName?.length > 0) {
      addMetaData({
        title: Base64.encode(displayName),
        description: Base64.encode(""),
      });
    }
  }, [addedFile?.id, displayName]);

  useEffect(() => {
    setHasChanges(hasChangedValues?.length > 0);
  }, [hasChangedValues]);

  const resetValues = () => {
    closeModal(true);
    setDisplayName("");
    setScheduledText("");
    setDoesRepeat(false);
    setDate(roundTimeToNearest15Minutes(new Date()));
    setRecipients(availableRecipients);
    removeFiles();
    setDisabled(() => false);
  };

  const save = async () => {
    if (!canPublish || !hasChanges || feedIds.length === 0) return;
    setDisabled(() => true);
    setSubmitError(() => null);
    try {
      let contentId = "";
      if (addedFile) {
        contentId = addedFile?.meta?.contentId;
        removeMetaData("feedId");
        await uppyClient.upload();
      }

      const workflowItem = await createWorkflowItem(workspaceId, contentId, displayName, scheduledText);
      const scheduledDateOrCron = calculateScheduledDateOrCron({
        date,
        dayOfWeek,
        doesRepeat,
      });
      const scheduledWorkflow: ScheduledWorkflowItem = {
        workspaceId,
        workflowItemId: workflowItem?.id,
        feedIds,
      };
      if (doesRepeat) {
        scheduledWorkflow.scheduledCron = scheduledDateOrCron;
        scheduledWorkflow.timezone = timezone;
      } else {
        scheduledWorkflow.scheduledDate = scheduledDateOrCron;
      }
      await createScheduledWorkflowItem(scheduledWorkflow);
      resetValues();
    } catch (e) {
      log.error("Failed to publish scheduled message", e);
      setSubmitError(() => "Failed to publish scheduled message. Please try again.");
      setDisabled(() => false);
    }
  };

  const update = async () => {
    if (feedIds.length === 0) return;
    setDisabled(() => true);
    setSubmitError(() => null);
    try {
      const payload = {
        workspaceId: workflowItem.workspaceId,
        scheduleId: workflowItem.scheduledTrigger.id,
        feedIds,
        ...cronTime,
      };
      await updateScheduledWorkflowItem(payload);
      resetValues();
    } catch (e) {
      log.error("Failed to update scheduled message", e);
      setError(() => true);
      setSubmitError(() => "Failed to update scheduled message. Please try again.");
      setDisabled(() => false);
    }
  };

  // Memoize handlers and derived activeChannels so that <SelectRecipients /> (React.memo'd) does
  // not re-render on every keystroke in the text fields above. Otherwise typing would cascade
  // through SelectGroupsOfFeeds / SelectNonGroups and re-render hundreds of MUI nodes per keypress.
  const setRecipientIds = useCallback((values: string[]) => {
    setRecipients(values.map((id) => ({ id, title: null })));
  }, []);

  const toggleAllRecipients = useCallback((ids: string[]) => {
    setRecipients((prev) => {
      const hasSome = ids.some((feedId) => prev?.map((i) => i.id).includes(feedId));
      if (hasSome) {
        return prev?.filter((i) => !ids.includes(i.id)) ?? [];
      }
      return [...prev, ...ids.map((id) => ({ id, title: null }))];
    });
  }, []);

  const activeChannelIds = useMemo(
    () => recipients?.map((i: { id?: string }) => i.id).filter((id): id is string => Boolean(id)) ?? [],
    [recipients],
  );

  const tabs = modalTabs
    ?.map((tab) => ({ ...tab, active: tab.key === activeTab }))
    ?.filter((tab) => !(hiddenTabs || []).includes(tab.key));

  const maxDisplayNameHelper = `Max ${internalNameCharLimit.toLocaleString()} characters. (${
    displayName?.length ?? 0
  }/${internalNameCharLimit})`;

  const DisabledBlock = ({ children }: { children: ReactNode }) => (
    <Box>
      <Box
        sx={{
          p: 2,
          width: "100%",
          borderRadius: "0.5rem",
          border: `solid 1px ${theme.palette.error.main}`,
          height: "100%",
          background: theme.palette.secondary.dark,
          display: "flex",
          alignItems: "center",
          textAlign: "center",
          justifyContent: "center",
        }}
      >
        {children}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ height: "480px", position: "relative" }}>
      <Box sx={{ mb: 2 }}>
        <ButtonTabs tabs={tabs} testId={TestIds.scheduled.tab} setActiveTab={(key) => setActiveTab(() => key)} />
      </Box>

      <Collapse in={activeTab === "generate"}>
        <Box sx={{ position: "relative", height: boxHeight }}>
          {addedFile?.id ? (
            <DisabledBlock>
              <Box>
                <Box sx={{ fontWeight: "bold" }}>Generate Audio Disabled</Box>
                <Box sx={{ mb: 1 }}>You can either have an attachment or generated audio, not both.</Box>
                <Box
                  onClick={() => removeFiles()}
                  component="button"
                  sx={{
                    background: theme.palette.secondary.main,
                    px: 1,
                    py: 0.5,
                  }}
                  className="basic-button"
                >
                  Remove Attachment
                </Box>
              </Box>
            </DisabledBlock>
          ) : (
            <>
              <FullInput
                label={
                  <Box>
                    <Typography sx={{ fontWeight: 700 }}>Internal Name*</Typography>
                    <Typography sx={{ fontSize: "14px" }}>Only you will be able to see this name.</Typography>
                  </Box>
                }
                sx={{
                  position: "relative",
                  width: "100%",
                  p: 0,
                  border: "solid 1px transparent",
                  "label + &": {
                    marginTop: "52px",
                  },
                }}
                value={displayName}
                callback={(e) => setDisplayName(e.target.value)}
                error={displayName?.length > internalNameCharLimit}
                helperText={maxDisplayNameHelper}
              />

              <TextMessageInput
                label="Enter text to generate audio"
                rows={6}
                error={!textValid}
                textInputValue={scheduledText}
                textInputHandler={(value: string) => setScheduledText(value)}
                validateTextHandler={(value: boolean) => setTextValid(value)}
              />
            </>
          )}
        </Box>
      </Collapse>

      <Collapse in={activeTab === "media"}>
        <Box sx={{ position: "relative", height: boxHeight }}>
          {scheduledText?.length > 0 ? (
            <DisabledBlock>
              <Box>
                <Box sx={{ fontWeight: "bold" }}>Add Media Disabled</Box>
                <Box sx={{ mb: 1 }}>You can either have an attachment or generated audio, not both.</Box>
                <Box
                  onClick={() => setScheduledText(() => "")}
                  component="button"
                  sx={{
                    background: theme.palette.secondary.main,
                    px: 1,
                    py: 0.5,
                  }}
                  className="basic-button"
                >
                  Clear Generated Audio
                </Box>
              </Box>
            </DisabledBlock>
          ) : (
            <>
              <FullInput
                label={
                  <Box>
                    <Typography sx={{ fontWeight: 700 }}>Internal Name*</Typography>
                    <Typography sx={{ fontSize: "14px" }}>Only you will be able to see this name.</Typography>
                  </Box>
                }
                sx={{
                  position: "relative",
                  width: "100%",
                  p: 0,
                  border: "solid 1px transparent",
                  "label + &": {
                    marginTop: "52px",
                  },
                }}
                value={displayName}
                callback={(e) => setDisplayName(e.target.value)}
                error={displayName?.length > internalNameCharLimit}
                helperText={maxDisplayNameHelper}
              />
              <Box>
                <HandleFileUpload
                  dontUpload={true}
                  dragDropId="workflow-modal-drag-drop"
                  statusBarId="workflow-modal-status-bar"
                />
              </Box>
            </>
          )}
        </Box>
      </Collapse>

      <Collapse in={activeTab === "date"}>
        <Box sx={{ height: boxHeight }}>
          <Box sx={{ display: "flex", gap: 2, mb: doesRepeat ? 3 : 0 }}>
            {!doesRepeat && (
              <Box sx={{ width: "50%" }}>
                <ScheduleDateTimePicker
                  label="Publish Date"
                  type="date"
                  dateTime={date}
                  saving={disabled}
                  setInvalidTime={(value) => setError(() => value)}
                  setDateTime={(value, dayOfWeek) => {
                    setDayOfWeek(() => (doesRepeat ? dayOfWeek : null));
                    setDate(() => new Date(value));
                  }}
                />
              </Box>
            )}
            <Box sx={{ width: "50%" }}>
              <ScheduleDateTimePicker
                type="time"
                label="Time"
                dateTime={date}
                saving={disabled}
                setInvalidTime={(value) => setError(() => value)}
                doesRepeat={doesRepeat}
                setDateTime={(date, dayOfWeek) => {
                  setDate(() => new Date(date));
                }}
              />
            </Box>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Box>Does this message repeat?</Box>
            <Box sx={{ mb: 2, display: "flex", gap: 2 }}>
              <label>
                <Radio
                  checked={doesRepeat}
                  onChange={() => {
                    setDoesRepeat(() => true);
                    setDayOfWeek(() => (dayOfWeek !== null ? dayOfWeek : 1));
                  }}
                />
                <span>Custom Recurrence</span>
              </label>
              <label>
                <Radio
                  checked={!doesRepeat}
                  onChange={() => {
                    setDoesRepeat(() => false);
                    setDayOfWeek(() => null);
                  }}
                />
                <span>Does Not Repeat</span>
              </label>
            </Box>

            <Box sx={{ display: doesRepeat ? "block" : "none" }}>
              <DaysOfWeekPicker
                disabled={!doesRepeat}
                activeIndex={dayOfWeek}
                label="Repeat every"
                selectedDays={doesRepeat ? [dayOfWeek] : []}
                daySelectionHandler={(value: number[]) => {
                  let initialValue = value[0] || 1;
                  if (!initialValue || initialValue === -1) {
                    initialValue = 0;
                  }
                  setDayOfWeek(() => initialValue);
                  const newDate = withDayOfWeek(date, initialValue - 1);
                  setDate(() => newDate);
                }}
                multiDay={false}
              />
            </Box>
          </Box>
        </Box>
      </Collapse>

      <Collapse in={activeTab === "channel"}>
        <Box sx={{ height: boxHeight }}>
          <SelectRecipients
            activeChannels={activeChannelIds}
            setActiveIds={setRecipientIds}
            toggleAllIds={toggleAllRecipients}
            allowNonAliasChannels={true}
          />
        </Box>
      </Collapse>

      <Box
        sx={{
          position: "sticky",
          display: "flex",
          flexDirection: "column",
          gap: 1,
          borderRadius: "1rem",
          left: 0,
          bottom: 0,
          background: theme.palette.secondary.dark,
        }}
      >
        {submitError && (
          <Alert severity="error" onClose={() => setSubmitError(() => null)}>
            {submitError}
          </Alert>
        )}
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            type="button"
            onClick={() => (hasChanges ? setConfirmDiscard(() => true) : resetValues())}
            variant="outlined"
            disabled={disabled}
          >
            Cancel
          </Button>
          {workflowItem?.id ? (
            <LoadingButton
              data-sb={TestIds.scheduled.publish}
              type="button"
              onClick={() => update()}
              loading={disabled}
              disabled={!updateSchedule || !hasChanges}
              variant={updateSchedule ? "contained" : "outlined"}
            >
              Update Schedule
            </LoadingButton>
          ) : (
            <LoadingButton
              type="button"
              onClick={() => save()}
              loading={disabled}
              disabled={!canPublish || !hasChanges}
              variant={canPublish ? "contained" : "outlined"}
            >
              Publish
            </LoadingButton>
          )}
        </Box>
      </Box>

      <ConfirmDiscardChanges
        id="discard-scheduled-message-edits"
        visible={confirmDiscard}
        text="Are you sure you want to discard these changes?"
        denyCloseHandler={() => {
          setConfirmDiscard(() => false);
        }}
        confirmCloseHandler={() => {
          resetValues();
        }}
      />
    </Box>
  );
}
